import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import apiClient from "../../services/api";
import { Program } from "../../types/program";
import { cambridgeSummerConfig } from "../../data/cambridgeSummerConfig";
import Hero from "../../components/programs/cambridge-summer-program/Hero";
import Introduction from "../../components/programs/cambridge-summer-program/Introduction";
import ProgramFeatures from "../../components/programs/cambridge-summer-program/ProgramFeatures";
import ProgramOverview from "../../components/programs/cambridge-summer-program/ProgramOverview";
import ProgramStructure from "../../components/programs/cambridge-summer-program/ProgramStructure";
import Training from "../../components/programs/cambridge-summer-program/Training";
import CTA from "../../components/programs/cambridge-summer-program/CTA";

const CAMBRIDGE_SUMMER_PROGRAM_SLUG = "cambridge-summer-program";
const CAMBRIDGE_ACADEMIC_INNOVATION_SLUG = "cambridge-academic-innovation-summer-program";

/**
 * Cambridge Summer Program page.
 * Shows 2-week and 4-week programs with Training section.
 */
const CambridgeSummerProgram: React.FC = () => {
    const config = cambridgeSummerConfig;
    const [searchParams] = useSearchParams();
    const [program2Weeks, setProgram2Weeks] = useState<Program | null>(null);
    const [program4Weeks, setProgram4Weeks] = useState<Program | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPrograms = async () => {
            try {
                setLoading(true);
                
                // Fetch both programs
                const [response2Weeks, response4Weeks] = await Promise.all([
                    apiClient.getProgram(CAMBRIDGE_ACADEMIC_INNOVATION_SLUG),
                    apiClient.getProgram(CAMBRIDGE_SUMMER_PROGRAM_SLUG)
                ]);

                // Handle 2-week program (sub program)
                if (response2Weeks.success) {
                    const data = response2Weeks?.data ?? response2Weeks;
                    const programData = data?.program ?? data;
                    if (programData && (programData.id || programData.slug || programData.title)) {
                        setProgram2Weeks(programData as Program);
                    }
                }

                // Handle 4-week program (main program)
                if (response4Weeks.success) {
                    const data = response4Weeks?.data ?? response4Weeks;
                    const programData = data?.program ?? data;
                    if (programData && (programData.id || programData.slug || programData.title)) {
                        setProgram4Weeks(programData as Program);
                    }
                }
            } catch (err) {
                console.error("Error fetching Cambridge programs:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchPrograms();
    }, []);

    // Handle scroll to training section when coming from programs page
    useEffect(() => {
        if (!loading && searchParams.get("scrollTo") === "training") {
            const timer = setTimeout(() => {
                const trainingSection = document.getElementById("training-section");
                if (trainingSection) {
                    trainingSection.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [loading, searchParams]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003C71] mx-auto mb-4" />
                    <p className="text-gray-600">Loading program details...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            <Hero config={config} program2Weeks={program2Weeks} program4Weeks={program4Weeks} />
            <Introduction config={config} />
            <ProgramFeatures config={config} />
            <Training program2Weeks={program2Weeks} program4Weeks={program4Weeks} />
            <ProgramOverview config={config} />
            <ProgramStructure config={config} />
            <CTA config={config} />
        </div>
    );
};

export default CambridgeSummerProgram;
